import "./FormTab.css";
import React, { useContext, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Form, Checkbox, SubmitButton } from "formik-antd";
import { isEqual, cloneDeep, isNull } from "lodash";
import { Button, Image, Skeleton, Tooltip } from "antd";
import { Formik } from "formik";

import InputDatepicker from "~/components/InputDatepicker/InputDatepicker";
import Select from "~/components/Select/Select";
import FieldAttachmentModal from "~/components/FieldAttachmentModal";
import FormikInput from "~/components/Input/FormikInput";
import UploadMidias from "~/components/UploadMidias";
import FieldModal from "~/components/FieldModal";
import {
  taskFieldAttachmentRoutes,
  TaskFieldRoutes,
  ListOptionsRoutes,
  WebhookRoutes,
} from "~/http/routes";
import { ToastContext } from "~/providers/ToastProvider";
import checkPermission from "~/helpers/checkPermission";
import { wrapUrlInAnchorTagWithIcon } from "~/helpers/stringUrlToLink";
import { LoadingOutlined } from "@ant-design/icons";

const MemoizedFormikInput = React.memo(
  ({ field, values }) => {
    const { id, label, type_id, required } = field;

    function getMask(fieldValue) {
      switch (type_id) {
        case 4:
          return "999.999.999-99";
        case 5:
          return "99.999.999/9999-99";
        case 13:
          if (fieldValue) {
            let mask =
              fieldValue.split("_").length === 2
                ? "(99) 9999-9999"
                : "(99)9 9999-9999";
            return mask;
          }
          return "(99)9 9999-9999";
        default:
          return "";
      }
    }

    return (
      <div className="mb-4">
        {required ? <span style={{ color: "red" }}>*</span> : ""}
        <FormikInput
          placeholder={label}
          name={id}
          placeholderTitle={label}
          inputType={
            type_id === 1 ? "money" : type_id === 3 ? "textArea" : "input"
          }
          type={type_id === 2 ? "number" : "text"}
          mask={getMask(values[id])}
          step="0.10"
          readOnly={
            type_id === 9 || type_id === 10 || type_id === 11 || type_id === 15
          }
          inputPlaceholder={
            type_id === 9 || type_id === 10 || type_id === 11
              ? "Para o preenchimento acesse o aplicativo Pilar.app"
              : type_id === 14
              ? "Separe os emails por vírgula ( , )"
              : type_id === 15
              ? "Campo Bloqueado"
              : null
          }
          placeholderClassName="input-placeholder d-inline-block text-truncate"
          labelBackground="#fff"
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.field.attachments, nextProps.field.attachments) &&
      prevProps.values[prevProps.field.id] ===
        nextProps.values[nextProps.field.id]
    );
  },
);

const MemoizedDateField = React.memo(
  ({ field, handleDateInputChange, required }) => {
    const { id, label } = field;
    return (
      <div className="mb-4">
        {required ? <span style={{ color: "red" }}>*</span> : ""}
        <InputDatepicker
          label={label}
          name={id}
          placeholder="Selecionar Data"
          labelBackground="white"
          inputReadOnly
          allowClear={false}
          onChange={(date) => handleDateInputChange(id, date)}
        />
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.field.value === nextProps.field.value,
);

const MemoizedCheckbox = React.memo(
  ({ field, required }) => {
    const { id, label } = field;
    return (
      <div className="mb-4">
        {required ? <span style={{ color: "red" }}>*</span> : ""}
        <Checkbox name={id}>{wrapUrlInAnchorTagWithIcon(label)}</Checkbox>
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.field.value === nextProps.field.value,
);

const MemoizedImageField = React.memo(
  ({
    field,
    taskId,
    subprojectId,
    afterSubmitAttachment,
    afterRemoveAttachment,
    required,
  }) => {
    const { id, label, attachments, withCaption } = field;
    return (
      <div className="mb-4">
        {required ? <span style={{ color: "red" }}>*</span> : ""}
        <ImageField
          id={id}
          label={label}
          attachments={attachments}
          withCaption={withCaption}
          taskId={taskId}
          subprojectId={subprojectId}
          afterSubmit={afterSubmitAttachment}
          afterRemove={afterRemoveAttachment}
        />
      </div>
    );
  },
);

const MemoizedSignatureField = React.memo(
  ({ field, required, attachments }) => {
    const { id, label } = field;
    return (
      <div className="mb-4">
        {required ? <span style={{ color: "red" }}>*</span> : ""}
        {attachments.length < 1 ? (
          <FormikInput
            inputPlaceholder="Para o preenchimento acesse o aplicativo Pilar.app"
            name={id}
            placeholder={label}
            readOnly
            placeholderClassName="input-placeholder d-inline-block text-truncate"
            labelBackground="#fff"
          />
        ) : (
          <>
            <p>{label}</p>
            <Image
              style={{
                border: "1px solid",
                borderRadius: "4px",
                borderColor: "#CCCC",
                padding: "10px",
              }}
              width="50%"
              src={attachments[0].url}
            />
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.attachments, nextProps.attachments),
);

const MemoizedGeolocationField = React.memo(
  ({ field, required }) => {
    const { id, label, value } = field;
    const GoogleMapsLink = ({ coordinates }) => {
      const [lat, lng] = coordinates.split(",").map((coord) => coord.trim());
      const url = `https://www.google.com/maps?q=${lat},${lng}`;

      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            fontSize: "18px",
          }}
        >
          <span
            className="material-icons"
            style={{
              fontSize: "30px",
            }}
          >
            location_on
          </span>
          Clique para ver a localização
        </a>
      );
    };

    return (
      <div className="mb-4">
        {required ? <span style={{ color: "red" }}>*</span> : ""}
        {isNull(value) ? (
          <FormikInput
            inputPlaceholder="Para obter a geolocalização acesse o aplicativo Pilar.app"
            name={id}
            placeholder={label}
            readOnly
            placeholderClassName="input-placeholder d-inline-block text-truncate"
            labelBackground="#fff"
          />
        ) : (
          <>
            <p>{label}</p>
            <GoogleMapsLink coordinates={value} />
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.field.value === nextProps.field.value,
);

const MemoizedSelectField = React.memo(
  ({ field, values, handleChange, optionList, required }) => {
    const { id, label, form_field_id } = field;

    const options = optionList
      .flat()
      .filter(
        (item) =>
          (item.form_id && item.form_id == form_field_id) ||
          (item.task_field_id && item.task_field_id == id),
      );

    return (
      <div className="mb-4">
        {required ? <span style={{ color: "red" }}>*</span> : ""}
        <Select
          key={values[id]}
          placeholder={label}
          id={id}
          value={values[id]}
          name={id}
          handleChange={handleChange}
          inputBackground="transparent"
          labelBackground="#fff"
          inputPlaceholder=""
          options={options}
          withNull={true}
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.optionList, nextProps.optionList) &&
      prevProps.values[prevProps.field.id] ===
        nextProps.values[nextProps.field.id]
    );
  },
);

const MemoizedWebhookField = React.memo(
  ({ field, values, isDirty, isWebhookLoading, handleWebhookClick, label }) => {
    const { id } = field;
    return (
      <Tooltip
        color="#888"
        title={isDirty(values) ? "Salve antes de enviar!" : ""}
      >
        <Button
          disabled={isDirty(values) || isWebhookLoading}
          className="w-100 mb-4"
          onClick={() => handleWebhookClick(id, label)}
          shape="round"
          style={{
            backgroundColor:
              isDirty(values) || isWebhookLoading ? "#d9d9d9" : "#28a745",
            color: isDirty(values) || isWebhookLoading ? "#888" : "white",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2px 40px",
            width: "100%",
            height: "40px",
          }}
        >
          <span
            className="material-icons-outlined"
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {isWebhookLoading ? (
              <LoadingOutlined size={8} />
            ) : (
              <span className="material-icons-outlined">play_circle</span>
            )}
          </span>
          {label}
        </Button>
      </Tooltip>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isDirty(prevProps.values) ===
        nextProps.isDirty(nextProps.values) &&
      prevProps.isWebhookLoading === nextProps.isWebhookLoading
    );
  },
);

const renderField = (field, values, handlers, state) => {
  const { type_id } = field;
  const {
    setFieldValue,
    handleChange,
    handleDateInputChange,
    handleAfterSubmitAttachment,
    handleAfterRemoveAttachment,
    handleWebhookClick,
  } = handlers;
  const { taskId, subprojectId, isDirty, isWebhookLoading, optionList } = state;

  const commonProps = {
    field,
    values,
    required: field.required,
  };

  switch (type_id) {
    case 6:
      return (
        <MemoizedDateField
          {...commonProps}
          handleDateInputChange={handleDateInputChange}
        />
      );
    case 7:
      return <MemoizedCheckbox {...commonProps} />;
    case 8:
    case 17:
      return (
        <MemoizedImageField
          {...commonProps}
          taskId={taskId}
          subprojectId={subprojectId}
          afterSubmitAttachment={handleAfterSubmitAttachment}
          afterRemoveAttachment={handleAfterRemoveAttachment}
        />
      );
    case 9:
    case 10:
      return (
        <MemoizedSignatureField
          {...commonProps}
          attachments={field.attachments}
        />
      );
    case 11:
      return <MemoizedGeolocationField {...commonProps} />;
    case 12:
      return (
        <MemoizedSelectField
          {...commonProps}
          handleChange={handleChange}
          optionList={optionList}
        />
      );
    case 16:
      return (
        <div className="mb-4">
          <Button
            className="w-100 mb-2"
            href={field.value}
            target="_blank"
            shape="round"
            style={{
              color: "#40A9FF",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2px 40px",
            }}
          >
            <span
              className="material-icons-outlined"
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              open_in_new
            </span>
            {field.label}
          </Button>
        </div>
      );
    case 18:
      return (
        <MemoizedWebhookField
          {...commonProps}
          isDirty={isDirty}
          isWebhookLoading={isWebhookLoading}
          handleWebhookClick={handleWebhookClick}
          label={field.label}
        />
      );
    default:
      return <MemoizedFormikInput {...commonProps} />;
  }
};

function ImageField(props) {
  const {
    id,
    label,
    attachments,
    subprojectId,
    taskId,
    afterSubmit,
    afterRemove,
    withCaption,
  } = props;
  const [attachment, setAttachment] = useState({
    id: "",
    filename: "",
    url: "",
    description: "",
    created_at: "",
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { setToast } = useContext(ToastContext);

  function handleBeforeUpload(file) {
    try {
      const isLt2M = file.size / 1024 / 1024 < 35;
      if (!isLt2M) {
        setToast({
          type: "error",
          title: "Erro ao carregar o arquivo!",
          message: "Arquivo deve ser menor que 35MB!",
          show: true,
          autohide: true,
        });
        return false;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setAttachment({
          id: "",
          filename: file.name,
          url: reader.result,
          description: "",
          created_at: "",
        });
        setIsModalVisible(true);
      };
    } catch (err) {
      setToast({
        type: "error",
        title: "Erro!",
        message: "Um erro fazer upload da imagem!",
        show: true,
        autohide: true,
      });
    }
  }

  function handlePreview(file) {
    const { id, url, description, created_at } = file;
    setAttachment({
      id,
      url,
      description,
      created_at: moment(created_at).format("DD/MM/YYYY HH:mm:ss"),
    });
    setIsModalVisible(true);
  }

  function handleAfterSubmit(attachment) {
    setIsModalVisible(false);
    if (afterSubmit) afterSubmit(attachment);
  }

  async function handleRemove(file) {
    try {
      if (checkPermission("nao-excluir-midia-formulario")) {
        setToast({
          type: "error",
          title: "Erro!",
          message: "Usuário sem permissão!",
          show: true,
          autohide: true,
        });
        return;
      }
      if (
        window.confirm(
          "Tem certeza que deseja excluir essa mídia?\n (Ao confirmar essa ação, a mídia será excluída diretamente do banco de dados)",
        )
      ) {
        await taskFieldAttachmentRoutes.delete(
          subprojectId,
          taskId,
          id,
          file.id,
        );
        setToast({
          type: "success",
          title: "Sucesso!",
          message: "Mídia excluída com sucesso!",
          show: true,
          autohide: true,
        });
        if (afterRemove) afterRemove(file);
      }
    } catch (err) {
      setToast({
        type: "error",
        title: "Erro!",
        message: "Um erro ao remover a mídia!",
        show: true,
        autohide: true,
      });
    }
  }

  return (
    <>
      <UploadMidias
        label={label}
        enableEditingDescription={true}
        fileList={attachments}
        onRemove={handleRemove}
        beforeUpload={handleBeforeUpload}
        onPreview={handlePreview}
        accept="*"
        taskId={taskId}
        subprojectId={subprojectId}
      />
      <FieldAttachmentModal
        withCaption={withCaption}
        visible={isModalVisible}
        id={attachment.id}
        filename={attachment.filename}
        url={attachment.url}
        description={attachment.description}
        createdAt={attachment.created_at}
        subprojectId={subprojectId}
        taskId={taskId}
        taskFieldId={id}
        onCancel={() => setIsModalVisible(false)}
        afterSubmit={handleAfterSubmit}
      />
    </>
  );
}

function FormTab(props) {
  const { taskId, subprojectId, afterSave, afterAddField } = props;
  const formRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [optionList, setOptionList] = useState([]);
  const [initialValues, setInitialValues] = useState({});
  const [fieldModalIsVisible, setFieldModalIsVisible] = useState(false);
  const { setToast } = useContext(ToastContext);
  const [isWebhookLoading, setIsWebhookLoading] = useState(false);
  const prevWebhookLoading = useRef(isWebhookLoading);

  const getData = async () => {
    setIsLoading(true);
    try {
      const res = await TaskFieldRoutes.list(taskId);
      setFields(res.data.data);
      setInitialValues(getInitialValues(res.data.data));
    } catch (err) {
      setToast({
        type: "error",
        title: "Erro!",
        message:
          "Um erro ocorreu ao carregar os campos do formulário da tarefa!",
        show: true,
        autohide: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (prevWebhookLoading.current === isWebhookLoading) {
      getData();
    }
    if (prevWebhookLoading.current && !isWebhookLoading) {
      getData();
    }
    prevWebhookLoading.current = isWebhookLoading;
  }, [isWebhookLoading]);

  useEffect(() => {
    setOptionList([]);
    async function fetchOptions() {
      try {
        const results = await Promise.all(
          fields
            .filter((item) => item.type_id === 12)
            .map(async (item) => {
              let options = await ListOptionsRoutes.findByTaskFieldsId(item.id);
              if (item.form_field_id) {
                options = await ListOptionsRoutes.find(item.form_field_id);
              }
              if (options) {
                return options.data.data.map((elem) => ({
                  value: elem.id,
                  name: elem.name,
                  form_id: elem.form_field_id,
                  task_field_id: elem.task_field_id,
                  type_id: 12,
                }));
              }
              return [];
            }),
        );
        const uniqueOptions = Array.from(
          new Map(results.flat().map((item) => [item.value, item])).values(),
        );
        setOptionList(uniqueOptions);
      } catch (err) {
        setToast({
          type: "error",
          title: "Erro!",
          message: "Um erro ocorreu ao buscar a lista de opções!",
          show: true,
          autohide: true,
        });
      }
    }
    fetchOptions();
  }, [fields]);

  const getFieldValue = (field) => {
    const { value, type_id, attachments } = field;
    if (type_id === 7) return value === "1";
    if (type_id === 8 || type_id === 17) return attachments;
    if (value === null) return "";
    return value;
  };

  const getInitialValues = (fields) => {
    const initialValues = {};
    fields.forEach((field) => {
      initialValues[field.id] = getFieldValue(field);
    });
    return initialValues;
  };

  const handleSubmit = async (values) => {
    try {
      const formattedFields = Object.entries(values)
        .filter(([, value]) => !Array.isArray(value))
        .map(([key, value]) => {
          let v = value;
          if (typeof value === "boolean") {
            v = value ? 1 : 0;
          } else if (!value && value !== 0) {
            v = null;
          }
          return { id: key, value: v };
        });
      await TaskFieldRoutes.updateMany(taskId, { fields: formattedFields });
      setInitialValues(values);
      setToast({
        type: "success",
        title: "Sucesso!",
        message: "Formulário salvo com sucesso!",
        show: true,
        autohide: true,
      });
      if (afterSave) afterSave();
    } catch (err) {
      setToast({
        type: "error",
        title: "Erro!",
        message: "Um erro ocorreu salvar o formulário!",
        show: true,
        autohide: true,
      });
    }
  };

  const handleValidate = (values) => {
    const errors = {};
    for (const [key, value] of Object.entries(values)) {
      const field = fields.find((e) => e.id === Number(key));
      if (!field) continue;

      if (field.type_id === 4 && value && value.replace("_", "").length < 14) {
        errors[key] = "CPF inválido";
      } else if (
        field.type_id === 5 &&
        value &&
        value.replace("_", "").length < 18
      ) {
        errors[key] = "CNPJ inválido";
      } else if (field.type_id === 14) {
        if (value) {
          const emailList = value.split(",");
          emailList.forEach((email) => {
            if (
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i.test(
                email.replace(" ", ""),
              )
            ) {
              errors[key] = "E-mail inválido";
            }
          });
        }
      } else if (field.type_id === 3 && value && value.length > 8000) {
        errors[key] = "Tamanho máximo de 8000 caracteres";
      }
    }
    return errors;
  };

  const isDirty = (values) => {
    const current = cloneDeep(values);
    const initial = cloneDeep(initialValues);
    for (const [key, value] of Object.entries(current)) {
      if (Array.isArray(value)) {
        delete current[key];
        delete initial[key];
      } else if (value === "___.___.___-__" || value === "__.___.___/____-__") {
        current[key] = "";
      }
    }
    return !isEqual(current, initial);
  };

  const handleAfterSubmitAttachment = (attachment) => {
    const updatedFields = fields.map((f) => {
      if (f.id == attachment.task_field_id) {
        const newAttachments = [...f.attachments];
        const attachmentIndex = newAttachments.findIndex(
          (e) => e.id === Number(attachment.id),
        );
        if (attachmentIndex === -1) {
          newAttachments.push(attachment);
        } else {
          newAttachments[attachmentIndex] = attachment;
        }
        return { ...f, attachments: newAttachments };
      }
      return f;
    });
    setFields(updatedFields);
  };

  const handleAfterRemoveAttachment = (attachment) => {
    const updatedFields = fields.map((f) => {
      if (f.id === attachment.task_field_id) {
        const newAttachments = f.attachments.filter(
          (e) => e.id !== Number(attachment.id),
        );
        return { ...f, attachments: newAttachments };
      }
      return f;
    });
    setFields(updatedFields);
  };

  const handleDateInputChange = (id, date) => {
    const formattedDate = date ? moment(date).format("YYYY-MM-DD") : null;
    formRef.current.setFieldValue(id, formattedDate);
  };

  const handleWebhookClick = async (id, label) => {
    try {
      setIsWebhookLoading(true);
      await WebhookRoutes.sendTaskInfo(taskId, id);
      setToast({
        type: "success",
        title: "Sucesso!",
        message: `${label} executado com sucesso!`,
        show: true,
        autohide: true,
      });
    } catch (err) {
      setToast({
        type: "error",
        title: "Erro",
        message: "Um erro inesperado ocorreu!",
        show: true,
        autohide: true,
      });
    } finally {
      setTimeout(() => {
        setIsWebhookLoading(false);
        if (afterSave) afterSave();
      }, 2000);
    }
  };

  const handleNewFieldButtonClick = () => setFieldModalIsVisible(true);
  const handleFieldModalCancel = () => setFieldModalIsVisible(false);

  const handleAfterSaveNewField = (field, setValues) => {
    setFields((prev) => [...prev, field]);
    setValues({
      ...formRef.current.values,
      [field.id]: getFieldValue(field),
    });
    setInitialValues({
      ...initialValues,
      [field.id]: getFieldValue(field),
    });
    setFieldModalIsVisible(false);
    if (afterAddField) afterAddField();
  };

  return (
    <div style={{ paddingRight: "24px" }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        innerRef={formRef}
        onSubmit={handleSubmit}
        validate={handleValidate}
      >
        {({ isSubmitting, values, setFieldValue, setValues, handleChange }) => (
          <Form>
            <Skeleton loading={isLoading} active>
              {fields.map((field) => (
                <div key={field.id}>
                  {renderField(
                    field,
                    values,
                    {
                      setFieldValue,
                      handleChange,
                      handleDateInputChange,
                      handleAfterSubmitAttachment,
                      handleAfterRemoveAttachment,
                      handleWebhookClick,
                    },
                    {
                      taskId,
                      subprojectId,
                      isDirty,
                      isWebhookLoading,
                      optionList,
                    },
                  )}
                </div>
              ))}
            </Skeleton>

            <Button
              className="w-100 mb-2"
              type="primary"
              onClick={handleNewFieldButtonClick}
              disabled={!checkPermission("criar-template-formulario")}
            >
              Novo campo
            </Button>

            <SubmitButton
              className="btn-default"
              disabled={!isDirty(values) || isSubmitting}
            >
              {!isSubmitting ? "Salvar" : " Salvando..."}
            </SubmitButton>

            <FieldModal
              visible={fieldModalIsVisible}
              taskId={taskId}
              onCancel={handleFieldModalCancel}
              afterSave={(field) => handleAfterSaveNewField(field, setValues)}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default FormTab;
